import React, { useState, useEffect } from "react";
import Divider from "@material-ui/core/Divider";
import ListIcon from "@material-ui/icons/List";
import AddIncidentButton from "./AddIncidentButton";
import IncidentItem from "./incidentItem";
import LabelVideoStore from "store/labelVideoStore";
import { observer } from "mobx-react";
import UiButton from "pages/common/UiButton";
import SkipDialog from "pages/common/SkipDialog";
import SkippedItemBanner from "pages/common/SkippedItemBanner";

const VIDEO_SKIP_I18N: Record<string, string> = {
  UNREADABLE: "Vidéo floue",
  CROPPED: "Vidéo tronquée",
  OCCLUSION: "Occlusion",
};

interface IncidentPanelProps {
  labelVideoStore: LabelVideoStore | undefined;
  onPause: () => void;
  onSave: () => void;
  onSkip: (motif: string) => void;
}

const IncidentPanel: React.FC<IncidentPanelProps> = ({
  labelVideoStore,
  onPause,
  onSave,
  onSkip,
}) => {
  const storeVideoMotif = labelVideoStore?.videoMotif ?? null;
  const incidentCount = labelVideoStore?.incidents.length ?? 0;
  const [localVideoMotif, setLocalVideoMotif] = useState<string | null>(
    storeVideoMotif
  );
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);

  useEffect(() => {
    if (incidentCount > 0) {
      setLocalVideoMotif(null);
    } else {
      setLocalVideoMotif(storeVideoMotif);
    }
  }, [incidentCount, storeVideoMotif]);

  return (
    <div
      style={{
        width: "30%",
        height: "calc(100vh - 158px)",
        padding: "8px",
        backgroundColor: "white",
        borderRadius: "8px",
      }}
    >
      <SkipDialog
        open={skipDialogOpen}
        motifSelected={labelVideoStore?.videoMotif || labelVideoStore?.motifs[0]}
        onAccept={(motif: string) => {
          setSkipDialogOpen(false);
          onSkip(motif);
        }}
        onClose={() => {
          setSkipDialogOpen(false);
        }}
        motifs={labelVideoStore?.motifs || []}
        i18nMapping={VIDEO_SKIP_I18N}
        defaultMotifIndex={0}
        isVideo={true}
      />
      <div
        style={{
          display: "flex",
          gap: "10px",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* HEADER */}
        <div style={{ display: "flex", justifyContent: "end" }}>
          {!labelVideoStore?.isReadOnly && (
            <AddIncidentButton onPause={onPause} />
          )}
        </div>

        <Divider style={{ backgroundColor: "rgb(241 241 241)" }} />

        {/* INCIDENTS */}
        <div
          style={{
            flexGrow: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: "5px",
            minHeight: 0,
          }}
        >
          {localVideoMotif ? (
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                minHeight: 0,
              }}
            >
              <SkippedItemBanner
                itemLabel="Vidéo"
                motifLabel={
                  VIDEO_SKIP_I18N[localVideoMotif] || localVideoMotif
                }
              />
            </div>
          ) : incidentCount === 0 ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: "#bdbdbd",
                fontSize: "1.1rem",
                padding: "32px 0",
                textAlign: "center",
              }}
            >
              <ListIcon style={{ fontSize: 62, marginBottom: 8 }} />
              <span style={{ fontSize: "1rem" }}>Pas encore de labels</span>
            </div>
          ) : (
            labelVideoStore?.incidents
              .slice()
              .reverse()
              .map((incident, index) => (
                <IncidentItem
                  key={"incident-" + index}
                  incident={incident}
                  onFinish={() => onPause()}
                />
              ))
          )}
        </div>


        {/* ACTIONS */}
        {!labelVideoStore?.isReadOnly && (
        <div
          style={{
            padding: "10px 0px 10px 0px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <UiButton
            variant="outlined"
            color="secondary"
            style={{ width: "48%" }}
            onClick={() => {
              setSkipDialogOpen(true);
            }}
            disabled={labelVideoStore?.isLoadingSave}
          >
            {labelVideoStore?.isLoadingSave ? "Sauvegarde..." : "Passer cette vidéo"}
          </UiButton>
          <UiButton
            variant="contained"
            color="secondary"
            style={{ width: "48%" }}
            onClick={onSave}
            disabled={
              labelVideoStore?.isLoadingSave ||
              !labelVideoStore?.incidents.length ||
              labelVideoStore?.incidents.some((inc) => !inc.isFinished)
            }
          >
            {labelVideoStore?.isLoadingSave ? "Sauvegarde..." : "Valider"}
          </UiButton>
        </div>
        )}
      </div>
    </div>
  );
};

export default observer(IncidentPanel);
